import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { useLocation, useRoute, useSearch } from "wouter";
import Header from "@/components/Header";
import { formatDistanceLabel, type ResolvedLocation } from "@/lib/geo";
import { ProductCard } from "@/components/ProductCard";
import { trackEvent } from "@/lib/analytics";
import { ClothingCard } from "@/components/ClothingCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isClothingItem,
  getClothingAttributes,
  getListingImages,
  garmentLabel,
  saleInfo,
  isFeatured as itemIsFeatured,
  GARMENT_TYPES,
  CLOTHING_SIZES,
} from "@shared/clothing";
import { ShoppingCart } from "@/components/ShoppingCart";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCart";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useSwipePager, type SwipeSibling } from "@/hooks/useSwipePager";
import { TapBurst, useTapBurst, useSurfaceDoubleTap } from "@/components/TapBurst";
import { ArrowLeft, Loader2, MapPin, Share2, Sparkles, Sprout, Store, Tag, Zap, X, MoreVertical, Flag, Ban, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { ReportDialog } from "@/components/ReportDialog";
import SellerReviews from "@/components/SellerReviews";
import { FoundingMakerBadge } from "@/components/FoundingMakerBadge";
import { stripDemoMarker } from "@/lib/demo";
import { tierStyle } from "@/lib/tierStyle";
import type { CartItemWithDetails } from "@shared/schema";
import { formatWeight, unitDisplayLabel } from "@shared/weight";

type PublicUser = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
  bio?: string | null;
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
  sellerType?: string | null;
  roles?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  zipCode?: string | null;
  canBuy?: boolean;
  canSell?: boolean;
  referralCount?: number;
  referralTier?: string | null;
  isFoundingMaker?: number | boolean | null;
};

type ProfileItem = {
  id: string;
  sellerId: string;
  name: string;
  category: string | null;
  zipCode: string;
  price: string;
  groceryAvg?: string | null;
  qtyAvailable: number;
  freshnessNote?: string | null;
  imageUrl?: string | null;
  isActive: number;
  subtype?: string | null;
  imagesJson?: string[] | null;
  attributesJson?: unknown;
  isFeatured?: number | null;
  salePrice?: string | null;
  createdAt?: string | null;
};

type BuyerFulfillmentOption = {
  id: string;
  fulfillmentMethod: "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  buyerLabel: string;
  description?: string | null;
  dayLabel?: string | null;
  timeSlots?: {
    id: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  }[];
  dropZone?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zipCode: string;
    pickupWindowLabel?: string | null;
  } | null;
};

function prettyLabel(value?: string | null) {
  if (!value) return null;
  if (value === "farmstand") return "Farm Stand";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default function Profile() {
  const [, params] = useRoute("/u/:id");
  const search = useSearch();
  const backHref = new URLSearchParams(search).get("back") || "";
  return <ProfileScreen userId={params?.id} backHref={backHref} />;
}

// preview renders the same page inert -- no header, no overlays, no analytics
// -- so the profile sliding in under a swipe paints exactly what lands.
function ProfileScreen({
  userId,
  backHref,
  preview = false,
}: {
  userId: string | undefined;
  backHref: string;
  preview?: boolean;
}) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isOwnProfile = Boolean(currentUser?.id && userId && currentUser.id === userId);
  const profileIdentityRef = useRef<HTMLDivElement | null>(null);
  const [buyerLocation] = useState<ResolvedLocation | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem("marketlink_buyer_location");
      return raw ? (JSON.parse(raw) as ResolvedLocation) : null;
    } catch {
      return null;
    }
  });
  const [showStickyIdentity, setShowStickyIdentity] = useState(false);
  const { cart, addToCart, updateQuantity, removeFromCart, clearCart, cartCount } = useCart();
  const { savedSet, toggleSave } = useSavedItems();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [viewer, setViewer] = useState<{ images: string[]; index: number } | null>(null);
  const [safetyMenuOpen, setSafetyMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const swipeStartX = useRef<number | null>(null);
  const swipeStartY = useRef<number | null>(null);

  const openViewer = (images: string[], index = 0) => {
    const cleaned = images.filter((u) => typeof u === "string" && u.trim().length > 0);
    if (cleaned.length === 0) return;
    setViewer({ images: cleaned, index: Math.min(Math.max(index, 0), cleaned.length - 1) });
  };
  const closeViewer = () => setViewer(null);
  const viewerStep = (dir: number) =>
    setViewer((v) =>
      v ? { ...v, index: (v.index + dir + v.images.length) % v.images.length } : v,
    );

  useEffect(() => {
    if (!viewer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeViewer();
      else if (e.key === "ArrowRight") viewerStep(1);
      else if (e.key === "ArrowLeft") viewerStep(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer]);

  const { data: cartItemsDetails = [] } = useQuery<CartItemWithDetails[]>({
    queryKey: ["/api/cart/details", JSON.stringify(cart)],
    queryFn: async () => {
      if (cart.length === 0) return [];
      const res = await fetch(`/api/cart/details?cart=${encodeURIComponent(JSON.stringify(cart))}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: cart.length > 0,
  });

  const handleAddToCart = (
    itemId: string,
    qty: number,
    maxQty?: number,
    weightUnit?: { id: string; weight: number; priceCents: number },
  ) => {
    if (weightUnit) {
      addToCart(itemId, 1, undefined, undefined, undefined, weightUnit);
      toast({ title: "Added to cart", description: unitDisplayLabel({ label: (weightUnit as any).label, weightCentiLb: weightUnit.weight }), duration: 5000 });
      return;
    }
    const existing = cart.find((entry) => entry.itemId === itemId)?.qty ?? 0;
    if (typeof maxQty === "number" && existing >= maxQty) {
      toast({
        title: "At stock limit",
        description: `Only ${maxQty} available from this seller.`,
      });
      return;
    }
    const added =
      typeof maxQty === "number" ? Math.max(0, Math.min(qty, maxQty - existing)) : qty;
    if (added <= 0) return;
    addToCart(itemId, added, maxQty);
    toast({
      title: "Added to cart",
      description: `${added} item${added > 1 ? "s" : ""} added to your cart`,
    });
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    setLocation("/checkout");
  };

  const profileQuery = useQuery<PublicUser>({
    queryKey: ["/api/users/profile", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const res = await fetch(`/api/users/${userId}/profile`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  useEffect(() => {
    const p = profileQuery.data;
    if (p && !preview) {
      trackEvent("seller_view", `/u/${(p as any).id}`, {
        sellerId: (p as any).id,
        displayName: (p as any).displayName,
        city: (p as any).city,
        state: (p as any).state,
        isFoundingMaker: (p as any).isFoundingMaker,
      });
    }
  }, [profileQuery.data]);

  const itemsQuery = useQuery<ProfileItem[]>({
    queryKey: ["/api/users/items", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const res = await fetch(`/api/users/${userId}/items`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  // Swiping moves through the growers list exactly as browse last rendered it
  // -- Home publishes the filtered, sorted order; re-implementing its filters
  // here would fork the browse contract. A maker who isn't in the last
  // rendered list (map tap, shared link, feed shelf) just doesn't swipe.
  const producerOrder = useMemo<SwipeSibling[]>(() => {
    if (preview || typeof window === "undefined") return [];
    try {
      const raw = window.sessionStorage.getItem("stoop.producerSwipeOrder");
      return raw ? (JSON.parse(raw) as SwipeSibling[]) : [];
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, userId]);
  const producerIndex = userId ? producerOrder.findIndex((entry) => entry.id === userId) : -1;
  const prevProducer = producerIndex > 0 ? producerOrder[producerIndex - 1] : null;
  const nextProducer =
    producerIndex >= 0 && producerIndex < producerOrder.length - 1
      ? producerOrder[producerIndex + 1]
      : null;

  const goToProducer = (target: SwipeSibling) => {
    // Replace, not push: back leaves to browse, never through swiped makers.
    setLocation(
      `/u/${target.id}${backHref ? `?back=${encodeURIComponent(backHref)}` : ""}`,
      { replace: true },
    );
  };

  const {
    rootRef: swipeRootRef,
    swipe,
    swipeBox,
    incoming,
    touchHandlers,
  } = useSwipePager({
    prev: prevProducer,
    next: nextProducer,
    disabled: preview,
    resetKey: userId,
    onCommit: goToProducer,
  });

  useEffect(() => {
    for (const sibling of [prevProducer, nextProducer]) {
      if (!sibling) continue;
      queryClient.prefetchQuery({
        queryKey: ["/api/users/profile", sibling.id],
        queryFn: async () => {
          const res = await fetch(`/api/users/${sibling.id}/profile`, { credentials: "include" });
          if (!res.ok) throw new Error(await res.text());
          return res.json();
        },
      });
      queryClient.prefetchQuery({
        queryKey: ["/api/users/items", sibling.id],
        queryFn: async () => {
          const res = await fetch(`/api/users/${sibling.id}/items`, { credentials: "include" });
          if (!res.ok) throw new Error(await res.text());
          return res.json();
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevProducer?.id, nextProducer?.id, queryClient]);

  // The component stays mounted across /u/A -> /u/B, so per-profile state
  // would otherwise ride along to the next maker.
  useEffect(() => {
    setViewer(null);
    setGarmentFilter("all");
    setSizeFilter("all");
    setStyleFilter("all");
    setOpenInfo(null);
    setSafetyMenuOpen(false);
    setReportOpen(false);
    setShowStickyIdentity(false);
  }, [userId]);

  const followQuery = useQuery<{ isFollowing: boolean }>({
    queryKey: ["/api/producers/follow", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const res = await fetch(`/api/producers/${userId}/follow`, { credentials: "include" });
      if (res.status === 401) return { isFollowing: false };
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const followerCountQuery = useQuery<{ count: number }>({
    queryKey: ["/api/producers/followers", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const res = await fetch(`/api/producers/${userId}/followers`);
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const user = profileQuery.data;
  const items = itemsQuery.data ?? [];
  const isFollowing = followQuery.data?.isFollowing ?? false;

  const isClothingShop = items.length > 0 && items.every((it) => isClothingItem(it));

  const [garmentFilter, setGarmentFilter] = useState<string>("all");
  const [sizeFilter, setSizeFilter] = useState<string>("all");
  const [styleFilter, setStyleFilter] = useState<string>("all");
  // Clothing shops keep About / fulfillment / reviews collapsed behind chips so
  // the grid leads — expand one inline on tap (null = all closed).
  const [openInfo, setOpenInfo] = useState<"about" | "fulfillment" | "reviews" | null>(null);
  const [clothingSort, setClothingSort] = useState<"newest" | "price-asc" | "price-desc">("newest");

  // Storefront gallery (opt-in per seller): every listing photo, deduped. For
  // one-product / portfolio makers who show many examples at one price.
  const galleryEnabled = (user as any)?.storefrontGalleryEnabled === 1;
  const galleryImages = useMemo(
    () =>
      galleryEnabled
        ? Array.from(new Set(items.flatMap((it) => getListingImages(it as any)))).slice(0, 60)
        : [],
    [galleryEnabled, items],
  );

  const founderDelivery = (user as any)?.founderDelivery as
    | { active: boolean; cap: number; minSubtotal: number }
    | null
    | undefined;
  const founderDeliveryNote = founderDelivery?.active ? (
    <div className="rounded-xl border border-emerald-600/30 bg-emerald-50/60 p-3 text-sm leading-6 text-emerald-900">
      🚚 <span className="font-medium">Free local delivery from Stoop</span> on orders $
      {founderDelivery.minSubtotal}+, complimentary for this maker's first {founderDelivery.cap}{" "}
      customers while we're getting started. Available in the local delivery zone.
    </div>
  ) : null;

  const presentGarmentTypes = useMemo(() => {
    const present = new Set(
      items.map((it) => getClothingAttributes(it).garmentType).filter(Boolean) as string[],
    );
    return GARMENT_TYPES.filter((g) => present.has(g.value));
  }, [items]);

  const presentSizes = useMemo(() => {
    const present = new Set(
      items.map((it) => getClothingAttributes(it).size).filter(Boolean) as string[],
    );
    return CLOTHING_SIZES.filter((s) => present.has(s));
  }, [items]);

  // Style/aesthetic tags actually used across this shop, ordered by frequency —
  // a curated discovery row (browse by y2k, grunge, etc.).
  const presentStyles = useMemo(() => {
    const counts = new Map<string, number>();
    for (const it of items) {
      for (const v of getClothingAttributes(it).vibes ?? []) {
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v);
  }, [items]);

  const featuredItems = useMemo(() => items.filter((it) => itemIsFeatured(it)), [items]);
  const saleItems = useMemo(() => items.filter((it) => saleInfo(it).onSale), [items]);

  // "New drops" = pieces listed in the last 14 days, newest first. Only shown
  // when the shop has a mix of new and older stock — otherwise (brand-new shop,
  // everything recent) it would just duplicate the whole grid, so it stays
  // hidden until it's meaningful.
  const newDrops = useMemo(() => {
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return items
      .filter((it) => it.createdAt && new Date(it.createdAt).getTime() >= cutoff)
      .sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime());
  }, [items]);
  const showNewDrops = newDrops.length > 0 && newDrops.length < items.length;

  const clothingItems = useMemo(() => {
    let filtered = items.filter((it) => {
      const attrs = getClothingAttributes(it);
      if (garmentFilter !== "all" && attrs.garmentType !== garmentFilter) return false;
      if (sizeFilter !== "all" && attrs.size !== sizeFilter) return false;
      if (styleFilter !== "all" && !(attrs.vibes ?? []).includes(styleFilter)) return false;
      return true;
    });
    if (clothingSort === "price-asc") {
      filtered = [...filtered].sort((a, b) => Number(a.price) - Number(b.price));
    } else if (clothingSort === "price-desc") {
      filtered = [...filtered].sort((a, b) => Number(b.price) - Number(a.price));
    }
    return filtered;
  }, [items, garmentFilter, sizeFilter, styleFilter, clothingSort]);

  const fulfillmentQueries = useQueries({
    queries: items.map((item) => ({
      queryKey: ["/api/items/fulfillment-options", item.id],
      queryFn: async (): Promise<BuyerFulfillmentOption[]> => {
        const res = await fetch(`/api/items/${item.id}/fulfillment-options`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      },
    })),
  });

  const fulfillmentOptions = fulfillmentQueries.flatMap((query) => query.data ?? []);
  const fulfillmentSummaryLoading =
    itemsQuery.isLoading || (items.length > 0 && fulfillmentQueries.some((query) => query.isLoading));

  const profileName =
    user?.displayName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    "Stoop Profile";

  const { burst, fire: fireBurst, clear: clearBurst } = useTapBurst();
  // Double tap only ever follows -- never unfollows -- and always shows the
  // burst, so a repeat confirms instead of silently undoing. Guests get the
  // sign-in toast from handleToggleFollow and no burst that lies.
  const followFromGesture = () => {
    if (preview || isOwnProfile || !userId) return;
    if (!currentUser) {
      toast({ title: "Sign in to follow sellers", variant: "destructive" });
      return;
    }
    if (!isFollowing) void handleToggleFollow();
    fireBurst("follow", `Following ${profileName}`);
  };
  const onSurfaceClick = useSurfaceDoubleTap(followFromGesture);

  const roleLabel = prettyLabel(user?.roles);
  const sellerTypeLabel = prettyLabel(user?.sellerType);

  const stickyLabel = sellerTypeLabel || roleLabel || null;

  const modeLabels = {
    seller_pickup: "Pickup from producer",
    drop_zone_pickup: "Community hub",
    local_delivery: "Local delivery",
  } as const;

  const availableModes = Array.from(
    new Set(fulfillmentOptions.map((option) => option.fulfillmentMethod))
  );

  const typicalDayLabels = Array.from(
    new Set(
      fulfillmentOptions
        .map((option) => option.dayLabel?.trim())
        .filter((value): value is string => Boolean(value) && value !== "Any day")
    )
  );

  const profileDistanceLabel = formatDistanceLabel({
    buyerLocation,
    producerLocation:
      user?.latitude != null &&
      user?.latitude !== "" &&
      user?.longitude != null &&
      user?.longitude !== ""
        ? {
            source: "producer_address",
            label: profileName,
            zipCode: user?.zipCode ?? null,
            point: {
              latitude: Number(user.latitude),
              longitude: Number(user.longitude),
            },
          }
        : null,
    fallbackBuyerZip: buyerLocation?.zipCode?.trim() || null,
    fallbackProducerZip: user?.zipCode ?? null,
  });

  const availabilitySummary =
    typicalDayLabels.length > 0
      ? `Typical availability: ${typicalDayLabels.slice(0, 3).join(" • ")}`
      : "Availability can vary by listing.";

  const communityHubNames = Array.from(
    new Set(
      fulfillmentOptions
        .map((option) => stripDemoMarker(option.dropZone?.name))
        .filter((value): value is string => Boolean(value))
    )
  );

  const hubSummary =
    communityHubNames.length > 0
      ? `Community hubs: ${communityHubNames.slice(0, 2).join(", ")}`
      : "Some items may be direct pickup or local delivery only.";

  const explicitBackTarget =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("back")
      : null;

  const handleBackToPrevious = () => {
    if (explicitBackTarget) {
      setLocation(explicitBackTarget);
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    setLocation("/?browse=producers");
  };

  const handleShareProfile = async () => {
    // Branded domain, not window.location.href — the wrapped app loads the raw
    // App Engine origin, so href would leak *.appspot.com. Share trystoop.com +
    // the profile path (drops any internal ?back= param).
    const shareUrl = `https://trystoop.com${window.location.pathname}`;
    const shareText = `Check out ${profileName} on Stoop`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: profileName,
          text: shareText,
          url: shareUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Link copied",
        });
        return;
      }

      toast({
        title: "Couldn't share",
        variant: "destructive",
      });
    } catch (error: any) {
      if (error?.name === "AbortError") return;

      toast({
        title: "Couldn't share",
        variant: "destructive",
      });
    }
  };

  const handleToggleFollow = async () => {
    if (!userId) return;

    try {
      const res = await fetch(`/api/producers/${userId}/follow`, {
        method: isFollowing ? "DELETE" : "POST",
        credentials: "include",
      });

      if (res.status === 401) {
        toast({
          title: "Sign in to follow sellers",
          variant: "destructive",
        });
        return;
      }

      if (res.status === 400) {
        toast({ title: "You can't follow yourself" });
        return;
      }

      if (!res.ok) throw new Error(await res.text());

      await followQuery.refetch();
      await followerCountQuery.refetch();

      toast({
        title: isFollowing ? "Removed" : "Saved",
      });
    } catch {
      toast({
        title: "Couldn't update follow",
        variant: "destructive",
      });
    }
  };

  const handleBlockUser = async () => {
    if (!userId) return;
    setSafetyMenuOpen(false);
    try {
      const res = await fetch("/api/block", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId }),
      });
      if (res.status === 401) {
        toast({ title: "Sign in to block users", variant: "destructive" });
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      toast({
        title: "Blocked",
        description: "They can no longer message you, and you won't see their content.",
      });
    } catch {
      toast({ title: "Couldn't block", variant: "destructive" });
    }
  };

  useEffect(() => {
    let ticking = false;

    const updateStickyState = () => {
      const node = profileIdentityRef.current;
      if (!node) return;

      const nextShow = node.getBoundingClientRect().bottom <= 56;
      setShowStickyIdentity((prev) => (prev === nextShow ? prev : nextShow));
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateStickyState);
    };

    updateStickyState();
    // Capture on document: the app scrolls in its own box now, and scroll
    // events from an element don't reach a window listener.
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    if (preview || typeof window === "undefined") return;
    if (window.location.hash !== "#reviews") return;
    setOpenInfo("reviews"); // clothing shops keep reviews behind a chip — open it
    const tryScroll = () => {
      const el = document.getElementById("reviews");
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    };
    if (tryScroll()) return;
    const attempts = [100, 300, 700];
    const timers = attempts.map((delay) =>
      window.setTimeout(() => {
        tryScroll();
      }, delay),
    );
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [userId]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {!preview && <Header cartCount={cartCount} onCartClick={() => setIsCartOpen(true)} />}

      <div className="flex flex-1 flex-col overflow-x-hidden">
      <main
        ref={swipeRootRef}
        className="flex-1"
        onClick={preview ? undefined : onSurfaceClick}
        {...touchHandlers}
        style={
          swipe.dx !== 0 || swipe.animating
            ? {
                transform: `translate3d(${swipe.dx}px, 0, 0)`,
                transition: swipe.animating ? "transform 180ms ease-out" : "none",
                willChange: "transform",
              }
            : undefined
        }
      >
        <div
          style={{ top: "calc(var(--safe-area-top, 0px) + 54px)" }}
          className={[
            "fixed inset-x-0 z-40 border-b border-border/60 bg-background shadow-sm",
            showStickyIdentity && swipe.dx === 0 ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-full opacity-0",
            "transition-transform transition-opacity duration-200",
          ].join(" ")}
        >
          <div className="container mx-auto max-w-5xl px-4">
            <div className="flex h-14 items-center gap-3">
              <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-border bg-muted">
                {user?.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt={profileName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-muted text-sm font-semibold text-muted-foreground">
                    {profileName.slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{profileName}</div>
                {stickyLabel && (
                  <div className="truncate text-xs text-muted-foreground">{stickyLabel}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="relative h-44 w-full overflow-hidden sm:h-56">
          {user?.coverImageUrl ? (
            <img
              src={user.coverImageUrl}
              alt={`${profileName} cover`}
              className="h-full w-full cursor-pointer object-cover"
              onClick={() => user.coverImageUrl && openViewer([user.coverImageUrl])}
              data-testid="img-cover-view"
            />
          ) : (
            <div className="h-full w-full bg-brand-gradient-soft" />
          )}
          <div className="pointer-events-none absolute inset-0 bg-image-overlay-dark" />

          <div className="absolute inset-x-0 top-3 z-20 px-4 sm:top-4">
            <div className="container mx-auto max-w-5xl">
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-full border-white/70 bg-background/90 shadow-sm backdrop-blur sm:h-9 sm:w-9"
                  onClick={handleBackToPrevious}
                  aria-label="Go back"
                  data-testid="button-back-to-producers"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>

                <div className="flex items-center gap-2">
                  {!isOwnProfile && (
                    <Button
                      type="button"
                      variant={isFollowing ? "default" : "outline"}
                      size="sm"
                      className={[
                        "rounded-full border-white/70 px-4 shadow-sm backdrop-blur",
                        isFollowing
                          ? "bg-primary text-primary-foreground"
                          : "bg-background/90",
                      ].join(" ")}
                      onClick={handleToggleFollow}
                      aria-label={isFollowing ? "Unfollow producer" : "Follow producer"}
                      data-testid="button-follow-producer"
                    >
                      {isFollowing ? "Following" : "Follow"}
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full border-white/70 bg-background/90 shadow-sm backdrop-blur sm:h-9 sm:w-9"
                    onClick={handleShareProfile}
                    aria-label="Share producer profile"
                    data-testid="button-share-producer"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>

                  {!isOwnProfile && (
                    <div className="relative">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full border-white/70 bg-background/90 shadow-sm backdrop-blur sm:h-9 sm:w-9"
                        onClick={() => setSafetyMenuOpen((v) => !v)}
                        aria-label="More options"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                      {safetyMenuOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setSafetyMenuOpen(false)}
                          />
                          <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-lg border bg-background text-foreground shadow-lg">
                            <button
                              type="button"
                              onClick={() => {
                                setSafetyMenuOpen(false);
                                setReportOpen(true);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                            >
                              <Flag className="h-4 w-4" /> Report
                            </button>
                            <button
                              type="button"
                              onClick={handleBlockUser}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-muted"
                            >
                              <Ban className="h-4 w-4" /> Block
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {!preview && !isOwnProfile && userId && (
              <ReportDialog
                open={reportOpen}
                onOpenChange={setReportOpen}
                targetType="user"
                targetId={userId}
                targetLabel={user?.displayName || "this seller"}
              />
            )}
          </div>
        </div>

        <div className="container mx-auto max-w-5xl px-4 pb-8">
          <div ref={profileIdentityRef} className="relative z-10 -mt-10 mb-6 sm:-mt-20">
            <div className="grid gap-3">

              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                {/* Keep the avatar stacked above the name at every width. On
                    viewports >=640px the old sm:flex-row + sm:items-end dropped
                    the name into the -mt cover overlap, making it unreadable over
                    the photo on wide screens (Z Fold, iPad). Verified with a
                    layout repro at 390/768/1024px. */}
                <div className="flex flex-col gap-3">
                  <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-background bg-muted shadow-lg sm:h-28 sm:w-28">
                    {user?.profileImageUrl ? (
                      <img
                        src={user.profileImageUrl}
                        alt={profileName}
                        className="h-full w-full cursor-pointer object-cover"
                        onClick={() => user.profileImageUrl && openViewer([user.profileImageUrl])}
                        data-testid="img-avatar-view"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-muted text-2xl font-semibold text-muted-foreground">
                        {profileName.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="min-w-0">
                    <h1 className="max-w-[14ch] text-[1.7rem] font-bold leading-[1.05] tracking-tight sm:max-w-none sm:text-3xl">
                      {profileName}
                    </h1>

                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                      {Boolean(user?.isFoundingMaker) && <FoundingMakerBadge />}

                      {user?.referralTier && (user.referralCount ?? 0) > 0 && (() => {
                        const style = tierStyle(user.referralTier);
                        return (
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold sm:px-2.5 sm:py-1 sm:text-sm ${style.className}`}
                            title={`${user.referralCount} ${user.referralCount === 1 ? "neighbor" : "neighbors"} invited`}
                          >
                            {style.icon} {user.referralTier}
                          </span>
                        );
                      })()}

                      {sellerTypeLabel && (
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1 sm:text-sm">
                          <Sprout className="mr-1 h-3.5 w-3.5 text-primary" />
                          {sellerTypeLabel}
                        </span>
                      )}

                      {roleLabel && (
                        <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1 sm:text-sm">
                          <Store className="mr-1 h-3.5 w-3.5 text-primary" />
                          {roleLabel}
                        </span>
                      )}

                      {profileDistanceLabel && (
                        <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1 sm:text-sm">
                          {profileDistanceLabel}
                        </span>
                      )}

                      {user?.zipCode && (
                        <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground sm:px-2.5 sm:py-1 sm:text-sm">
                          <MapPin className="mr-1 h-3.5 w-3.5 text-primary" />
                          {user.zipCode}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 text-sm text-muted-foreground">
                <span><span className="font-medium text-foreground">{items.length}</span> listing{items.length === 1 ? "" : "s"}</span>
                {followerCountQuery.data != null && (
                  <span><span className="font-medium text-foreground">{followerCountQuery.data.count}</span> follower{followerCountQuery.data.count === 1 ? "" : "s"}</span>
                )}
              </div>

              {isClothingShop && items.length > 0 && (
                <Button
                  type="button"
                  className="w-full sm:w-auto sm:self-start"
                  onClick={() =>
                    document
                      .getElementById("shop-listings")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" })
                  }
                >
                  Shop the collection · {items.length} pieces
                </Button>
              )}
            </div>
          </div>

          {(profileQuery.isLoading || itemsQuery.isLoading) && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading profile…
            </div>
          )}

          {user && (
            <div className="mb-5 space-y-2">
              <div className="flex flex-wrap gap-2">
                <FilterChip
                  label="About"
                  active={openInfo === "about"}
                  onClick={() => setOpenInfo(openInfo === "about" ? null : "about")}
                />
                <FilterChip
                  label="How to get items"
                  active={openInfo === "fulfillment"}
                  onClick={() => setOpenInfo(openInfo === "fulfillment" ? null : "fulfillment")}
                />
                {user.canSell && userId && (
                  <FilterChip
                    label="Reviews"
                    active={openInfo === "reviews"}
                    onClick={() => setOpenInfo(openInfo === "reviews" ? null : "reviews")}
                  />
                )}
              </div>

              {openInfo === "about" && (
                <div className="rounded-xl border border-primary/15 bg-card-gradient p-4 text-sm leading-6 text-muted-foreground">
                  {stripDemoMarker(user.bio) || "No bio yet."}
                </div>
              )}

              {openInfo === "fulfillment" && (
                <div className="rounded-xl border border-primary/15 bg-card-gradient p-4">
                  {fulfillmentSummaryLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </div>
                  ) : fulfillmentOptions.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {availableModes.map((mode) => (
                          <span
                            key={mode}
                            className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-foreground"
                          >
                            {modeLabels[mode]}
                          </span>
                        ))}
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-2xl border border-border/70 bg-background/70 p-3">
                          <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                            <CalendarDays className="h-4 w-4 text-primary" />
                            When to expect it
                          </div>
                          <div className="mt-1 text-sm leading-6 text-muted-foreground">
                            {availabilitySummary}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-border/70 bg-background/70 p-3">
                          <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                            <MapPin className="h-4 w-4 text-primary" />
                            Where to grab it
                          </div>
                          <div className="mt-1 text-sm leading-6 text-muted-foreground">
                            {hubSummary}
                          </div>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        Based on active listings. Exact options vary by item.
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Fulfillment details appear once there are active listings.
                    </p>
                  )}
                  {founderDeliveryNote && <div className="mt-3">{founderDeliveryNote}</div>}
                </div>
              )}

              {openInfo === "reviews" && user.canSell && userId && (
                <div
                  id="reviews"
                  className="scroll-mt-[calc(var(--safe-area-top,0px)+120px)] rounded-xl border border-primary/15 bg-card-gradient p-4"
                >
                  <SellerReviews sellerId={userId} />
                </div>
              )}
            </div>
          )}

          {galleryImages.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Photos
              </h3>
              <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
                {galleryImages.map((url, i) => (
                  <button
                    key={`${url}-${i}`}
                    type="button"
                    onClick={() => openViewer(galleryImages, i)}
                    className="aspect-square overflow-hidden rounded-lg border"
                  >
                    <img src={url} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <section
            id="shop-listings"
            className="scroll-mt-[calc(var(--safe-area-top,0px)+110px)]"
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Listings</h2>
              <span className="text-sm text-muted-foreground">{items.length} active</span>
            </div>

            {isClothingShop ? (
              <>
                {showNewDrops && (
                  <div className="mb-5">
                    <div className="mb-2 flex items-center gap-1.5">
                      <Zap className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide">New drops</h3>
                    </div>
                    <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {newDrops.map((item) => (
                        <div key={item.id} className="w-40 shrink-0 sm:w-44">
                          <ClothingCard
                            item={{ ...item, name: stripDemoMarker(item.name) || item.name }}
                            href={`/items/${item.id}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {featuredItems.length > 0 && (
                  <div className="mb-5">
                    <div className="mb-2 flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide">Neighborhood favorites</h3>
                    </div>
                    <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {featuredItems.map((item) => (
                        <div key={item.id} className="w-40 shrink-0 sm:w-44">
                          <ClothingCard
                            item={{ ...item, name: stripDemoMarker(item.name) || item.name }}
                            href={`/items/${item.id}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {saleItems.length > 0 && (
                  <div className="mb-5">
                    <div className="mb-2 flex items-center gap-1.5">
                      <Tag className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide">On Sale</h3>
                    </div>
                    <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {saleItems.map((item) => (
                        <div key={item.id} className="w-40 shrink-0 sm:w-44">
                          <ClothingCard
                            item={{ ...item, name: stripDemoMarker(item.name) || item.name }}
                            href={`/items/${item.id}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(showNewDrops || featuredItems.length > 0 || saleItems.length > 0) && (
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Shop all
                  </h3>
                )}

                {(presentGarmentTypes.length > 0 || presentSizes.length > 0 || presentStyles.length > 0) && (
                  <div
                    className="sticky z-30 -mx-4 mb-4 space-y-2 border-b border-border/60 bg-background px-4 py-2"
                    style={{ top: "calc(var(--safe-area-top, 0px) + 110px)" }}
                  >
                    {presentGarmentTypes.length > 0 && (
                      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <FilterChip
                          label="All"
                          active={garmentFilter === "all"}
                          onClick={() => setGarmentFilter("all")}
                        />
                        {presentGarmentTypes.map((g) => (
                          <FilterChip
                            key={g.value}
                            label={garmentLabel(g.value) ?? g.label}
                            active={garmentFilter === g.value}
                            onClick={() => setGarmentFilter(g.value)}
                          />
                        ))}
                      </div>
                    )}
                    {presentSizes.length > 0 && (
                      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <FilterChip
                          label="All sizes"
                          active={sizeFilter === "all"}
                          onClick={() => setSizeFilter("all")}
                        />
                        {presentSizes.map((s) => (
                          <FilterChip
                            key={s}
                            label={s}
                            active={sizeFilter === s}
                            onClick={() => setSizeFilter(s)}
                          />
                        ))}
                      </div>
                    )}
                    {presentStyles.length > 0 && (
                      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <FilterChip
                          label="All styles"
                          active={styleFilter === "all"}
                          onClick={() => setStyleFilter("all")}
                        />
                        {presentStyles.map((s) => (
                          <FilterChip
                            key={s}
                            label={`#${s}`}
                            active={styleFilter === s}
                            onClick={() => setStyleFilter(s)}
                          />
                        ))}
                      </div>
                    )}
                    <div className="flex justify-end">
                      <Select value={clothingSort} onValueChange={(v) => setClothingSort(v as typeof clothingSort)}>
                        <SelectTrigger className="h-8 w-[170px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Newest</SelectItem>
                          <SelectItem value="price-asc">Price: Low to High</SelectItem>
                          <SelectItem value="price-desc">Price: High to Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <div className="grid min-h-screen grid-cols-2 gap-3 content-start sm:grid-cols-3">
                  {clothingItems.map((item) => (
                    <ClothingCard
                      key={item.id}
                      item={{ ...item, name: stripDemoMarker(item.name) || item.name }}
                      href={`/items/${item.id}`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((item) => (
                  <ProductCard
                    key={item.id}
                    item={{ ...item, name: stripDemoMarker(item.name) || item.name, seller: user as any } as any}
                    onAddToCart={handleAddToCart}
                    buyerLocation={buyerLocation}
                    fallbackBuyerZip={buyerLocation?.zipCode?.trim() || null}
                    isSaved={savedSet.has(item.id)}
                    onToggleSave={toggleSave}
                    cartQty={cart.find((entry) => entry.itemId === item.id)?.qty ?? 0}
                  />
                ))}
              </div>
            )}

            {!itemsQuery.isLoading && items.length === 0 && isOwnProfile && (
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">You won't appear in the app until you list a product</p>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        Snap a photo and we'll fill in the details for you, or add one yourself.
                      </p>
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <Button onClick={() => setLocation("/seller?action=add")}>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Snap a photo to list
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setLocation("/seller?action=add-manual")}
                        >
                          Add it manually
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {!itemsQuery.isLoading && items.length === 0 && !isOwnProfile && (
              <Card>
                <CardContent className="pt-6 text-sm text-muted-foreground">
                  No active listings yet.
                </CardContent>
              </Card>
            )}
          </section>
        </div>
        {producerIndex >= 0 && producerOrder.length > 1 && (
          <div className="mx-auto max-w-5xl px-4 pb-6">
            <div className="flex items-center gap-2 rounded-2xl border border-border/70 bg-background/70 p-2">
              <button
                type="button"
                onClick={() => prevProducer && goToProducer(prevProducer)}
                className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-xl px-2.5 py-2 text-left text-xs transition ${
                  prevProducer ? "text-foreground hover:bg-muted" : "invisible"
                }`}
                data-testid="button-prev-producer"
              >
                <ChevronLeft className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{prevProducer?.name}</span>
              </button>
              <span className="shrink-0 px-1 text-[11px] text-muted-foreground">
                {producerIndex + 1} of {producerOrder.length}
              </span>
              <button
                type="button"
                onClick={() => nextProducer && goToProducer(nextProducer)}
                className={`flex min-w-0 flex-1 items-center justify-end gap-1.5 rounded-xl px-2.5 py-2 text-right text-xs transition ${
                  nextProducer ? "text-foreground hover:bg-muted" : "invisible"
                }`}
                data-testid="button-next-producer"
              >
                <span className="truncate">{nextProducer?.name}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </div>
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              Swipe to browse growers &amp; makers
            </p>
          </div>
        )}
      </main>
      </div>

      {!preview && incoming && swipeBox.width > 0 && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-30 overflow-hidden"
          style={{ top: swipeBox.top }}
          aria-hidden="true"
        >
          <div
            className="h-full w-full overflow-hidden bg-background"
            style={{
              transform: `translate3d(${
                swipe.dx + (incoming.fromRight ? swipeBox.width : -swipeBox.width)
              }px, 0, 0)`,
              transition: swipe.animating ? "transform 180ms ease-out" : "none",
              willChange: "transform",
            }}
          >
            <ProfileScreen userId={incoming.id} backHref={backHref} preview />
          </div>
        </div>
      )}

      {!preview && <TapBurst burst={burst} onDone={clearBurst} />}

      {!preview && (
      <>
      <ShoppingCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        items={cartItemsDetails}
        onUpdateQuantity={updateQuantity}
        onRemove={removeFromCart}
        onClearCart={clearCart}
        onCheckout={handleCheckout}
      />

      {viewer && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={closeViewer}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          data-testid="image-viewer-overlay"
          onTouchStart={(e) => {
            swipeStartX.current = e.touches[0].clientX;
            swipeStartY.current = e.touches[0].clientY;
          }}
          onTouchEnd={(e) => {
            const sx = swipeStartX.current;
            const sy = swipeStartY.current;
            swipeStartX.current = null;
            swipeStartY.current = null;
            if (sx == null || sy == null) return;
            const dx = e.changedTouches[0].clientX - sx;
            const dy = e.changedTouches[0].clientY - sy;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
              viewerStep(dx < 0 ? 1 : -1);
            } else if (dy > 90 && Math.abs(dx) < 60) {
              closeViewer();
            }
          }}
        >
          <button
            type="button"
            className="absolute right-4 top-[calc(1rem+var(--safe-area-top,0px))] flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
            onClick={closeViewer}
            aria-label="Close image preview"
          >
            <X className="h-5 w-5" />
          </button>

          {viewer.images.length > 1 && (
            <>
              <button
                type="button"
                className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                onClick={(e) => {
                  e.stopPropagation();
                  viewerStep(-1);
                }}
                aria-label="Previous photo"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                onClick={(e) => {
                  e.stopPropagation();
                  viewerStep(1);
                }}
                aria-label="Next photo"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
              <div className="absolute bottom-[calc(1rem+var(--safe-area-bottom,0px))] left-1/2 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur">
                {viewer.index + 1} / {viewer.images.length}
              </div>
            </>
          )}

          <img
            src={viewer.images[viewer.index]}
            alt="Full size"
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      </>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-muted-foreground hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}
